<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading"
        @cancel="closeModal"
        @submit="buyReward"
      >
        <h1>{{ $t("pages.driver.pointList.modal.buyReward.title") }}</h1>
      </modal-header>
    </template>

    <div class="modal-body mt-8">
      <form-card class="mt-7">
        <template #title>
          {{ $t("pages.driver.pointList.modal.buyReward.selectRewardTitle") }}
        </template>
        <template #description>
          {{ $t("pages.driver.pointList.modal.buyReward.selectRewardDescription") }}
        </template>
        <field
          :label="t('pages.driver.pointList.modal.buyReward.selectReward')"
          :empty="selectedReward === undefined || selectedReward === null"
          :variant="'standard'"
        >
          <multiselect
            ref="multiselectRef"
            v-model="selectedReward"
            class="multi-select"
            :options="rewards"
            :searchable="true"
            :clearable="true"
            label="label"
            @update:model-value="selectedReward = $event"
          />
        </field>
      </form-card>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import { ref, computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import Modal from '@utils/Modal/Modal.vue';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import Field from '@components/utils/inputs/Field.vue';
import Multiselect from '@vueform/multiselect';
import { useVModel } from '@vueuse/core';
import { useProvider } from '@utils/table/hooks/provider';
import { useQueryRef } from '@hooks/url';
import type { Driver, PointReward, HydraCollection } from '@/entities/Entities';

interface Props {
  open: boolean;
  driver: Driver;
}

const emit = defineEmits(['update:open', 'bought']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);

const sortKey = useQueryRef<string | undefined>('sortKey', 'timestamp');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const { t } = useI18n();
const notify = useNotify();

const selectedReward = ref(null);

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
});

const { data: rewardsData } = useProvider<HydraCollection<PointReward>>(
  '/point_rewards',
  providerParams,
  { immediate: true },
);

const rewards = computed(() => rewardsData.value?.['hydra:member'].map((reward) => ({
  value: reward.id,
  label: `${reward.name} (${t('pages.driver.pointList.modal.buyReward.cost')}: ${reward.cost} ${t('pages.driver.pointList.modal.buyReward.points')})`,
})) || []);

function resetData() {
  selectedReward.value = null;
}

function closeModal() {
  resetData();
  internalOpen.value = false;
}

const [exec, { loading }] = useFetchAction();

const buyReward = async () => {
  if (!selectedReward.value) {
    notify?.({
      variant: 'error',
      title: t('pages.driver.pointList.modal.buyReward.noRewardSelectedError'),
    });
    return;
  }

  try {
    const url = `/point_rewards/${selectedReward.value}/buy`;

    await exec(url, {
      method: 'POST',
      body: {
        driver: props.driver['@id'],
      },
    });

    notify?.({
      variant: 'success',
      title: t('pages.driver.pointList.modal.buyReward.buySuccess'),
    });
    emit('bought');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.driver.pointList.modal.buyReward.buyError'),
    });
  }
};
</script>

<style scoped lang="scss">
.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.btn {
  min-width: 100px;
  margin-right: 10px;
}
</style>
